import React from 'react';
import propTypes from 'prop-types';
import { CircleX, Check, X } from 'lucide-react';
import Button from 'tailwind-components/Button';
import { isSelfAgent, getCannotOffboardSelfMessage } from 'helpers/agents';

function OffboardingActionsCell({
  record,
  currentUserId,
  mainZohoId,
  viewAsAgent,
  userAgencyId,
  onOpenOffboard,
  onRequestSecondApproval
}) {
  const isSelf = isSelfAgent({
    agentId: record.agentId,
    currentUserId,
    mainZohoId,
    viewAsAgent
  });

  // Second approval buttons
  if (record.showSecondApprovalBtns) {
    const belongs =
      String(userAgencyId) === String(record.secondApprovalAgency?.id);
    let disabled = true;
    let helperMessage = record.secondApprovalHelperText;

    if (isSelf) {
      helperMessage = 'You cannot approve or deny your own offboarding.';
    } else if (viewAsAgent) {
      helperMessage =
        'You cannot perform this action while on View as Agent mode.';
    } else if (belongs) {
      disabled = false;
      helperMessage = null;
    }

    return (
      <div className='flex flex-col items-center gap-1 py-1 px-2'>
        <div className='flex flex-row gap-2'>
          <Button
            size='xsmall'
            variant='danger'
            text='Deny'
            icon={<X />}
            disabled={disabled}
            aria-disabled={disabled ? 'true' : undefined}
            onClick={
              disabled
                ? undefined
                : () => onRequestSecondApproval('deny', record)
            }
          />
          <Button
            size='xsmall'
            variant='success'
            text='Approve'
            icon={<Check />}
            disabled={disabled}
            aria-disabled={disabled ? 'true' : undefined}
            onClick={
              disabled
                ? undefined
                : () => onRequestSecondApproval('approve', record)
            }
          />
        </div>
        {helperMessage && (
          <p className='m-0 max-w-44 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
            {helperMessage}
          </p>
        )}
      </div>
    );
  }

  // Can offboard
  if (record.canOffboard) {
    const agentBelongsToMyAgency =
      String(userAgencyId) === String(record.agencyId);

    if (isSelf) {
      const message = getCannotOffboardSelfMessage({
        agentId: record.agentId,
        currentUserId,
        viewAsAgent
      });
      return (
        <div className='flex flex-col items-center gap-1 py-1 px-2'>
          <Button
            size='xsmall'
            variant='danger'
            text='Offboard'
            disabled
            aria-disabled='true'
            icon={<CircleX />}
          />
          {message && (
            <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
              {message}
            </p>
          )}
        </div>
      );
    }

    if (!agentBelongsToMyAgency) {
      return (
        <div className='flex flex-col items-center gap-1 py-1 px-2'>
          <Button
            size='xsmall'
            variant='danger'
            text='Offboard'
            disabled
            aria-disabled='true'
            icon={<CircleX />}
          />
          <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
            Only admins from {record.agencyName} can approve this request.
          </p>
        </div>
      );
    }

    return (
      <div className='flex flex-col items-center gap-1 py-1 px-2'>
        <Button
          size='xsmall'
          variant='danger'
          text='Offboard'
          icon={<CircleX />}
          onClick={() => onOpenOffboard(record.agentId)}
        />
        {record.helperText && (
          <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-orange-500'>
            {record.helperText}
          </p>
        )}
      </div>
    );
  }

  // Non-actionable: disabled offboard button + backend helperText
  return (
    <div className='flex flex-col items-center gap-1 py-1 px-2'>
      <Button
        size='xsmall'
        variant='danger'
        text='Offboard'
        disabled
        aria-disabled='true'
        icon={<CircleX />}
      />
      {record.helperText && (
        <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
          {record.helperText}
        </p>
      )}
    </div>
  );
}

OffboardingActionsCell.propTypes = {
  record: propTypes.object.isRequired,
  currentUserId: propTypes.string,
  mainZohoId: propTypes.string,
  viewAsAgent: propTypes.string,
  userAgencyId: propTypes.string,
  onOpenOffboard: propTypes.func.isRequired,
  onRequestSecondApproval: propTypes.func.isRequired
};

export default OffboardingActionsCell;
